import { useCallback } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import { useBrandsFollowingIds } from 'src/utils/Hooks/reduxSelectors';

/**
 * Hook to get filters configuration from app state
 */
export const useBeaconFilters = () => {
  const filters = useAppSelector((state) => state.filters);

  const { category, subcategory, segment, businessUnits, retailPrice } = filters || {};

  const numberOfActiveFilters =
    (category?.length || 0) + (subcategory?.length || 0) + (segment?.id ? 1 : 0) + (businessUnits?.length || 0);

  return {
    hasActiveFilters: !!category || !!subcategory || !!segment || !!businessUnits || !!retailPrice,
    numberOfActiveFilters,
    activeFilterDisplayNames:
      numberOfActiveFilters > 0
        ? numberOfActiveFilters === 1
          ? '1 Selection'
          : `${numberOfActiveFilters} Selections`
        : null,
    appFilters: {
      categoryFilters:
        category || subcategory
          ? Array.from(new Set([...(category ? category.map((filteredCategory) => +filteredCategory.id) : [])]))
          : null,
      subcategoryFilters: subcategory ? subcategory.map((filteredSubcategory) => +filteredSubcategory.id) : null,
      segmentFilters: segment
        ? {
            id: (segment as any).id,
            dn: (segment as any).dn,
            termFilters: (segment as any).termFilters ? (segment as any).termFilters : [],
            rangeFilters: (segment as any).rangeFilters ? (segment as any).rangeFilters : []
          }
        : null,
      businessUnitFilters: businessUnits || null,
      retailPriceFilter: retailPrice || null
    }
  };
};

/**
 * A hook that applies entity filters to Doris API request parameters.
 * This is similar to useAtlasV2ApplyEntityFilters but adapted for Doris APIs.
 */
export const useDorisApplyEntityFilters = () => {
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const entityId = useAppSelector((state) => state?.entityService?.mainEntity?.id);
  const brandIds = useBrandsFollowingIds();
  const { hasActiveFilters, appFilters } = useBeaconFilters();
  const atlasSegmentGroups = useAppSelector((state) => state.filters?.atlasSegmentGroups as string[][] | null);
  const savedSearchesById = useAppSelector((state) => state.segments?.savedSearchesById || new Map());

  return useCallback(
    (
      requestParams: {
        categoryIds?: number[];
        subcategoryIds?: number[];
        brandIds?: number[];
        segmentIds?: string[][];
        minRetailPrice?: number;
        maxRetailPrice?: number;
        businessunitids?: string[];
        stacklineSkus?: string[];
        retailerId?: number;
        metricType?: string;
        startWeekId?: number;
        exportType?: string;
        fieldName?: string;
        endWeekId?: number;
        comparisonStartWeekId?: number;
        comparisonEndWeekId?: number;
        trendIds?: string[];
        // Export params
        shouldGroupByWeek?: boolean;
        fileName?: string;
      },
      showMarketShare = false
    ) => {
      const params = {
        ...requestParams,
        // Ensure these are always arrays even if undefined
        categoryIds: requestParams.categoryIds || [],
        subcategoryIds: requestParams.subcategoryIds || [],
        // Use brandIds from following list as default, if not explicitly provided
        brandIds: requestParams.brandIds?.length ? requestParams.brandIds : [],
        segmentIds: requestParams.segmentIds || []
      };

      // Apply entity-specific filters
      if (entityType === 'product') {
        // For product, we keep product-specific filters
        // but don't need brand filters since the product already has a specific brand
        params.brandIds = [];
        params.stacklineSkus = [entityId as string];
      } else if (entityType === 'brand') {
        // Override brandIds with the current entity ID
        params.brandIds = [entityId as number];
      } else if (entityType === 'category') {
        // Override categoryIds with the current entity ID (if not "All Categories")

        if (!showMarketShare) {
          params.brandIds = [];
        }
        if (Number(entityId) === 0) {
          params.categoryIds = [];
        } else {
          params.categoryIds = [entityId as number];
        }
        // For category view, use the default following brandIds
        // params.brandIds is already set from earlier
      } else if (entityType === 'subcategory') {
        if (!showMarketShare) {
        }
        // Override subcategoryIds with the current entity ID
        params.subcategoryIds = [entityId as number];
        // For subcategory view, use the default following brandIds
        // params.brandIds is already set from earlier
      } else if (entityType === 'segment' || entityType === 'searchtermlist') {
        params.segmentIds = [[entityId as string]];
      } else if (entityType === 'company') {
        params.brandIds = brandIds;
      } else if (entityType === 'businessunit') {
        params.businessunitids = [entityId as string];
      } else if (entityType === 'microtrend') {
        params.trendIds = [entityId as string];
      }

      // Apply active URL filters that override entity filters

      if (hasActiveFilters) {
        if (appFilters.categoryFilters) {
          params.categoryIds = appFilters.categoryFilters as number[];
        }

        if (appFilters.subcategoryFilters) {
          params.subcategoryIds = appFilters.subcategoryFilters as number[];
        }

        if (atlasSegmentGroups && atlasSegmentGroups.length > 0) {
          // Atlas parent-grouped segments: [[seg1, seg2], [seg3]] -> (seg1 OR seg2) AND (seg3)
          const newGroups: string[][] = [];
          // Preserve entity segment if viewing a segment entity
          if (entityType === 'segment' || entityType === 'searchtermlist') {
            newGroups.push([entityId as string]);
          }
          atlasSegmentGroups.forEach((group) => newGroups.push([...group]));
          params.segmentIds = newGroups;
        } else if (appFilters.segmentFilters && !appFilters.businessUnitFilters) {
          // Only add the segment filter if we don't have businessUnitFilters
          // (businessUnitFilters will handle all segments)
          params.segmentIds.push([appFilters.segmentFilters.id]);
        }

        // Apply retailPrice filters if available
        if (appFilters.retailPriceFilter) {
          params.minRetailPrice = Number((appFilters.retailPriceFilter as any).minValue) || undefined;
          params.maxRetailPrice = Number((appFilters.retailPriceFilter as any).maxValue) || undefined;
        }

        // This is weird logic, but basically for doris we would want just pass Id instead of the query itself like we used to do in AdvancedSearch,
        // So we will specify the businessUnitIds and segmentIds that we want to use for the request
        if (appFilters.businessUnitFilters || (atlasSegmentGroups && atlasSegmentGroups.length > 0)) {
          const businessUnitIds = params.businessunitids ? [...params.businessunitids] : [];

          // Preserve entity segment if viewing a segment entity
          const entitySegmentId =
            entityType === 'segment' || entityType === 'searchtermlist' ? (entityId as string) : null;

          // Create a map to track segments by their parent BU
          const segmentsByParentBU = new Map<string, string[]>();

          // Helper function to find parent BU of a segment
          const findParentBU = (segmentId: string): string | null => {
            for (const [id, entity] of savedSearchesById.entries()) {
              if (entity?.type === 'businessunit' && entity.children?.includes(segmentId)) {
                return id;
              }
            }
            return null;
          };

          // Process all items in businessUnitFilters
          appFilters.businessUnitFilters.forEach((businessUnit) => {
            const entity = savedSearchesById.get(businessUnit);

            if (entity?.type === 'segment') {
              // Find which BU this segment belongs to
              const parentBU = findParentBU(businessUnit);

              if (parentBU) {
                // Use parent BU as the grouping key
                if (!segmentsByParentBU.has(parentBU)) {
                  segmentsByParentBU.set(parentBU, []);
                }
                segmentsByParentBU.get(parentBU)!.push(businessUnit);
              } else {
                // No parent BU found, use the segment ID itself as key to keep it separate
                segmentsByParentBU.set(`orphan_${businessUnit}`, [businessUnit]);
              }
            } else if (entity?.type === 'businessunit') {
              // Instead of adding the BU to businessUnitIds, add all its children segments
              const children = entity.children || [];

              // Find the parent of this business unit to group its children correctly
              const parentOfThisBU = findParentBU(businessUnit);
              const groupKey = parentOfThisBU || businessUnit; // Use parent if exists, otherwise use the BU itself

              // Add all child segments to the appropriate group
              children.forEach((childId) => {
                const childEntity = savedSearchesById.get(childId);
                if (childEntity?.type === 'segment') {
                  if (!segmentsByParentBU.has(groupKey)) {
                    segmentsByParentBU.set(groupKey, []);
                  }
                  segmentsByParentBU.get(groupKey)!.push(childId);
                }
              });
            }
          });

          params.businessunitids = businessUnitIds;

          const newSegmentIds: string[][] = [];

          if (entitySegmentId) {
            newSegmentIds.push([entitySegmentId]);
          }

          if (atlasSegmentGroups && atlasSegmentGroups.length > 0) {
            atlasSegmentGroups.forEach((group) => {
              if (group.length > 0) newSegmentIds.push(group);
            });
          } else {
            for (const segmentGroup of segmentsByParentBU.values()) {
              if (segmentGroup.length > 0) {
                newSegmentIds.push(segmentGroup);
              }
            }
          }

          params.segmentIds = newSegmentIds;
        }
      }

      // Final sanity check to ensure we always return arrays, not undefined
      return {
        ...params,
        categoryIds: params.categoryIds || [],
        subcategoryIds: params.subcategoryIds || [],
        brandIds: params.brandIds || [],
        segmentIds: params.segmentIds || [],
        businessunitids: params.businessunitids || []
      };
    },
    [entityType, hasActiveFilters, appFilters, entityId, brandIds, savedSearchesById, atlasSegmentGroups]
  );
};
